import { FC, useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import ReusableModal from '../common/Modal';
import theme from '../../theme';
import { addToBlacklist } from '../../utils/apis';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface BlacklistNumberModalProps {
  open: boolean;
  onClose: () => void;
  row: { from: string; to: string };
  showAlert: (message: string, severity: 'success' | 'error') => void;
  onSuccess?: () => void;
}

const commonSelectStyles = {
  height: '40px',
  '& .MuiSelect-select': {
    display: 'flex',
    padding: '8px 14px',
    alignItems: 'center',
    borderRadius: '6px',
    border: '1px solid #DCE0E7',
    background: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 'normal',
    letterSpacing: '-0.14px',
    color: theme.palette.lightGray.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
};

const commonInputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: '#FFFFFF',
    '& fieldset': {
      border: '1px solid #DCE0E7',
      borderRadius: '6px',
    },
    '&:hover fieldset': {
      borderColor: '#DCE0E7',
    },
    '&.Mui-focused fieldset': {
      borderWidth: '1px',
      borderColor: '#DCE0E7',
    },
  },
  '& input, & textarea': {
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 14px',
  },
};

const BlacklistNumberModal: FC<BlacklistNumberModalProps> = ({
  open,
  onClose,
  row,
  showAlert,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [selectedOption, setSelectedOption] = useState<'from' | 'to'>('from');
  const [reason, setReason] = useState('');

  const selectedNumber = selectedOption === 'from' ? row.from : row.to;

  const { mutate: addMutation, isPending: isAddPending } = useMutation({
    mutationFn: addToBlacklist,
    onSuccess: () => {
      showAlert('Number added to blacklist successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['agentCallsData'] });
      onSuccess?.();
      handleClose();
    },
    onError: (err: any) => {
      showAlert(
        err?.response?.data?.message || 'Failed to add number to blacklist',
        'error'
      );
    },
  });

  const handleClose = () => {
    setReason('');
    setSelectedOption('from');
    onClose();
  };

  useEffect(() => {
    if (open) {
      setSelectedOption('from');
      setReason('');
    }
  }, [open]);

  const handleOptionChange = (event: SelectChangeEvent<'from' | 'to'>) => {
    setSelectedOption(event.target.value as 'from' | 'to');
    setReason('');
  };

  const handleAdd = () => {
    if (!reason.trim()) return;
    addMutation({ phoneNumber: selectedNumber, reason: reason.trim() });
  };

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title="Blacklist number"
      width={440}
    >
      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <InputLabel sx={{ mb: 1, fontSize: '14px' }}>
            Select number
          </InputLabel>
          <Select
            fullWidth
            value={selectedOption}
            onChange={handleOptionChange}
            sx={commonSelectStyles}
            variant="outlined"
          >
            <MenuItem value="from">From: {row.from}</MenuItem>
            <MenuItem value="to">To: {row.to}</MenuItem>
          </Select>
        </Box>

        <Box>
          <InputLabel sx={{ mb: 1, fontSize: '14px' }} required>
            Reason
          </InputLabel>
          <TextField
            fullWidth
            placeholder="Enter reason for blacklisting"
            value={reason}
            onChange={e => setReason(e.target.value)}
            multiline
            minRows={2}
            sx={{
              ...commonInputStyles,
              '& .MuiOutlinedInput-root': {
                padding: '0px',
                minHeight: '40px',
                height: 'auto',
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            pt: 1,
            borderTop: '1px solid #DCE0E7',
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              color: theme.palette.custom?.textBlue || 'inherit',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!reason.trim() || isAddPending}
            startIcon={
              isAddPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              height: '40px',
              boxShadow: 'none',
            }}
          >
            {isAddPending ? 'Adding...' : 'Add to blacklist'}
          </Button>
        </Box>
      </Box>
    </ReusableModal>
  );
};

export default BlacklistNumberModal;
